import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const SITE_URL = 'https://allchinabuy.codes';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const isGuide = slug === 'allchinabuy-buying-guide';
  const articleUrl = `${SITE_URL}/${slug}/`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.h1,
    description: article.metaDescription,
    image: article.heroImage,
    datePublished: '2026-06-01',
    dateModified: '2026-06-25',
    author: {
      '@type': 'Organization',
      name: 'AllChinaBuy Codes Editorial Team',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'AllChinaBuy Codes',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: articleUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="relative w-full min-h-[55vh] md:min-h-[65vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.heroImage}
            alt={article.title}
            title={article.h1}
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/45"></div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center mt-10">
          <span className="inline-block px-4 py-1.5 border border-background-50/30 rounded-full text-background-50 text-xs md:text-sm font-medium mb-5 tracking-wide">
            {isGuide ? 'Shopping Guide' : 'Article'} &middot; Updated {article.updatedDate}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 font-bold leading-tight mb-5">
            {article.h1}
          </h1>
          <p className="text-background-50/80 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            {article.subtitle}
          </p>
          <div className="flex items-center justify-center gap-4 mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-50/15 backdrop-blur-sm border border-background-50/30 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/25 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              allchinabuy spreadsheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 md:px-10 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            <article className="flex-1 max-w-none lg:max-w-[65%]">
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">{article.title}</span>
              </nav>

              <div className="flex items-center gap-3 mb-8 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  Updated: {article.updatedDate}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-team-line"></i>
                  Reviewed by Editorial Team
                </span>
              </div>

              <div className="prose-custom space-y-6 text-foreground-700 text-sm md:text-base leading-relaxed">
                {article.sections.map((section, si) => (
                  <div key={si}>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
                      {section.heading}
                    </h2>
                    {section.content.map((para, pi) => (
                      <p key={pi} className={pi === 0 ? 'mb-4' : 'mb-4'}>
                        {para}
                      </p>
                    ))}
                  </div>
                ))}

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 my-6">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2 bg-background-100 rounded-lg border border-background-200/60 text-sm text-foreground-700 hover:text-primary-600 hover:border-primary-200 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-external-link-line text-xs"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-12 p-6 bg-primary-50 rounded-2xl border border-primary-100">
                <p className="text-sm text-primary-800 font-medium mb-2">
                  Ready to start exploring?
                </p>
                <p className="text-sm text-primary-700 mb-4">
                  Visit our homepage to browse all categories and discover the complete AllChinaBuy Spreadsheet directory with the latest updates for 2026.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line"></i>
                  Back to Homepage
                </Link>
              </div>

              <div className="mt-8 flex items-center gap-6 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  Last Updated: {article.updatedDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-team-line"></i>
                  Reviewed by Editorial Team
                </span>
              </div>
            </article>

            <aside className="lg:w-[30%] flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                <div className="bg-background-100 rounded-2xl p-6 border border-background-200/60">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                    Quick Navigation
                  </h3>
                  <div className="flex flex-col gap-2">
                    <Link to="/" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-home-line mr-2"></i>Homepage
                    </Link>
                    <Link to="/#categories" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-grid-line mr-2"></i>All Categories
                    </Link>
                    <Link to="/#safety" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-shield-check-line mr-2"></i>Safety Guidelines
                    </Link>
                    <Link to="/allchinabuy-buying-guide/" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-guide-line mr-2"></i>Shopping Guide
                    </Link>
                  </div>
                </div>

                <div className="bg-background-100 rounded-2xl p-6 border border-background-200/60">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                    Resources & Links
                  </h3>
                  <div className="flex flex-col gap-3">
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      allchinabuy spreadsheet
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Buy It Now
                      <i className="ri-shopping-bag-line"></i>
                    </a>
                  </div>
                </div>

                <div className="relative rounded-2xl overflow-hidden aspect-[3/4]">
                  <img
                    src={article.sidebarImage}
                    alt={`${article.title} Resources`}
                    title={`${article.h1} Resources`}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-background-50/90 backdrop-blur-sm rounded-full text-xs font-medium text-foreground-800">
                      {isGuide ? 'Guide' : 'Article'}
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                    <p className="text-background-50 text-sm font-medium">Verified Sources</p>
                    <p className="text-background-50/70 text-xs">Weekly Checks</p>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </div>
  );
}